/**
 * What a customer's link looks like in a text message, before they tap it.
 *
 * A phone previewing a link reads the page's HTML and nothing else, and every
 * route in this app is served from one `index.html` whose `<title>` is "Roof
 * Board" — the name of a piece of software the customer has never heard of. So
 * each link a customer is sent has its own card here, and a Cloudflare Pages
 * Function (`functions/<route>/[[path]].ts`) writes it onto that route's HTML
 * on the way out. Any other path gets null and is served untouched.
 *
 * Jonathon, 2026-09-22, of the intake link: "instead of the attached thumbnail
 * reading like that can it say something like click here to enter your
 * contact info" (ADR 0204).
 *
 * Jonathon, 2026-10-21, of the estimate link: "it should say click here to see
 * estimate instead of just roofboard", with balance requests and change orders
 * given their own matching wording.
 */

import { COMPANY_NAME } from "./constants.js";

export interface LinkPreview {
  title: string;
  description: string;
}

/**
 * The name on every card. A preview is drawn by a Pages Function that cannot
 * read `company_settings`, so it comes from `COMPANY_NAME`, the fallback, in
 * the long form CLAUDE.md rule 11 asks for, less the trailing "LLC".
 */
export const PREVIEW_SITE_NAME = COMPANY_NAME.replace(/,?\s+LLC\.?$/, "");

/** The square copper monogram. */
export const PREVIEW_IMAGE = "/logo-copperridge.png";

export const INTAKE_PREVIEW: LinkPreview = {
  title: "Click here to enter your contact info",
  description:
    `${PREVIEW_SITE_NAME} — tell us how to reach you and when suits, ` +
    "and we will get you booked in for a free inspection.",
};

export const ESTIMATE_PREVIEW: LinkPreview = {
  title: "Click here to see your estimate",
  description: `${PREVIEW_SITE_NAME} — your estimate from us, ready to read.`,
};

export const BALANCE_PREVIEW: LinkPreview = {
  title: "Click here to see your balance",
  description: `${PREVIEW_SITE_NAME} — what is left to pay on your job, and how to pay it.`,
};

export const CHANGE_ORDER_PREVIEW: LinkPreview = {
  title: "Click here to see your change order",
  description: `${PREVIEW_SITE_NAME} — the added work on your job, ready to read and sign.`,
};

export const CERTIFICATE_PREVIEW: LinkPreview = {
  title: "Click here to sign your certificate of completion",
  description: `${PREVIEW_SITE_NAME} — the work at your home is finished; please read and sign.`,
};

/**
 * The query a balance request adds to the estimate link.
 *
 * A balance request opens the very same `/p/<token>` page as the estimate, so
 * the path alone cannot tell the two apart; this marker is the only
 * difference, and the page itself ignores it.
 */
export const BALANCE_LINK_QUERY = "for=balance";

/** The estimate page's link with the balance marker on it. */
export function balanceLink(estimateLink: string): string {
  return `${estimateLink}${estimateLink.includes("?") ? "&" : "?"}${BALANCE_LINK_QUERY}`;
}

const TOKEN = "[0-9a-f]{32}";
const ESTIMATE_PATH = new RegExp(`^/p/${TOKEN}/?$`);
const CHANGE_ORDER_PATH = new RegExp(`^/c/${TOKEN}/?$`);
const CERTIFICATE_PATH = new RegExp(`^/cert/${TOKEN}/?$`);
const INTAKE_PATH = /^\/go(\/|$)/;

/** The card for this link, or null for any path that is not a customer's. */
export function linkPreviewFor(url: URL): LinkPreview | null {
  const path = url.pathname;
  if (INTAKE_PATH.test(path)) return INTAKE_PREVIEW;
  if (CHANGE_ORDER_PATH.test(path)) return CHANGE_ORDER_PREVIEW;
  if (CERTIFICATE_PATH.test(path)) return CERTIFICATE_PREVIEW;
  if (ESTIMATE_PATH.test(path)) {
    return url.searchParams.get("for") === "balance"
      ? BALANCE_PREVIEW
      : ESTIMATE_PREVIEW;
  }
  return null;
}

const escape = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * The Open Graph and Twitter tags for a card. The image is absolute because a
 * preview is fetched by a server that has no page to be relative to.
 */
export function linkPreviewTags(preview: LinkPreview, origin: string): string {
  const title = escape(preview.title);
  const description = escape(preview.description);
  const image = escape(`${origin}${PREVIEW_IMAGE}`);
  return (
    `<meta property="og:title" content="${title}">` +
    `<meta property="og:description" content="${description}">` +
    `<meta property="og:image" content="${image}">` +
    `<meta property="og:site_name" content="${escape(PREVIEW_SITE_NAME)}">` +
    `<meta property="og:type" content="website">` +
    `<meta name="twitter:card" content="summary_large_image">` +
    `<meta name="twitter:title" content="${title}">` +
    `<meta name="twitter:description" content="${description}">` +
    `<meta name="twitter:image" content="${image}">`
  );
}
